import type {
  AvailabilityDoctorSummary,
  AvailabilityBookingOptionSummary,
  AvailabilityTenantSummary,
  AvailabilityDoctorCenter,
  AvailabilityCalendarWindow,
  LocalizedPriceSummary,
  ResourceScheduleDay,
} from "@/services/naaviApi/types";
import { collectScheduleHours } from "@/helpers/scheduleHelper";
import {
  ATTENDANCE_MODE,
  type AttendanceMode,
} from "@/constants/attendanceMode";

function toAttendanceMode(
  option: AvailabilityBookingOptionSummary | undefined,
): AttendanceMode {
  if (option?.attendanceMode !== undefined) {
    return option.attendanceMode;
  }
  return option?.allowRemote === true
    ? ATTENDANCE_MODE.BOTH
    : ATTENDANCE_MODE.IN_PERSON;
}

type TranslateFn = (key: string) => string;

export interface DoctorBookingOption {
  key: string;
  bookingTypeId: number;
  bookingTypeName: string;
  bookingTypeDescription: string | null;
  bookingTypeImage: string | null;
  label: string;
  price: string;
  priceLocalized: LocalizedPriceSummary | null;
  depositDisplay: string | null;
  priceIsFrom: boolean;
  durationMinutes: number | null;
  bookingByPlace: boolean;
  allowCreateBookByPlace: boolean;
  allowRemote: boolean;
  attendanceMode: AttendanceMode;
  schedule: ResourceScheduleDay[];
  hours: string[];
  calendarWindow: AvailabilityCalendarWindow | null;
  tenant: AvailabilityTenantSummary;
  centerId: number | null;
  centerKey: string;
  location: {
    name: string;
    address: string;
  };
  resourceId: number;
  userId: number | null;
  scheduleLoaded: boolean;
  scheduleLoading: boolean;
  scheduleError: string | null;
}

export interface DoctorCenter {
  key: string;
  centerId: number | null;
  tenantId: string;
  tenantXId: string;
  tenantName: string;
  name: string;
  address: string;
  url: string | null | undefined;
  variantKeys: string[];
  latitude: number | null | undefined;
  longitude: number | null | undefined;
}

export interface DoctorAvailability {
  id: string;
  entryKey: string;
  slug: string | null;
  resourceId: number;
  tenantId: string;
  tenantXId: string;
  tenantName: string;
  tenantLogo: string | null | undefined;
  tenantUrl: string | null | undefined;
  name: string;
  image: string | null | undefined;
  specialty: string;
  appointmentDate: string | null;
  mainAddress: string | null | undefined;
  tenantAddress: string | null | undefined;
  paymentMethod: string | null | undefined;
  services: string[];
  aboutMe: string[];
  education: string[];
  reviews: unknown[];
  consultationFee: string | null;
  personalizedService: boolean;
  position: {
    lat: number | null | undefined;
    lng: number | null | undefined;
  };
  bookingOptions: DoctorBookingOption[];
  centers: DoctorCenter[];
  classes: unknown[];
  totalDatesMax: number;
  totalTimesMax: number;
  distanceKm: number | null | undefined;
  isOnline: boolean;
  baseDateIso: string;
  rawSummary: AvailabilityDoctorSummary;
  searchCycleId?: number;
}

export interface MapAvailabilityOptions {
  baseDateIso: string;
  translate?: TranslateFn;
}

export function mapAvailabilitySummariesToDoctors(
  summaries: AvailabilityDoctorSummary[],
  options: MapAvailabilityOptions,
): DoctorAvailability[] {
  const translate = options.translate ?? ((key: string) => key);
  const baseDateIso = options.baseDateIso;

  return summaries.map((summary) =>
    mapDoctorSummary(summary, baseDateIso, translate),
  );
}

function mapDoctorSummary(
  summary: AvailabilityDoctorSummary,
  baseDateIso: string,
  translate: TranslateFn,
): DoctorAvailability {
  const entryKey = resolveDoctorEntryKey(summary);
  const tenantSummary = createTenantSummary(summary);
  const centers = Array.isArray(summary.centers) ? summary.centers : [];

  const bookingOptions = buildBookingOptions(summary, tenantSummary, centers);
  const centerEntries = buildCenters(
    summary,
    tenantSummary,
    centers,
    bookingOptions,
    translate,
  );

  const firstCenter = centerEntries[0];
  const position = {
    lat: firstCenter?.latitude ?? summary.tenantLatitude ?? null,
    lng: firstCenter?.longitude ?? summary.tenantLongitude ?? null,
  };

  const doctorId = summary.userId ?? summary.resourceId;

  return {
    id: String(doctorId),
    entryKey,
    slug: summary.userSlug ?? null,
    resourceId: summary.resourceId,
    tenantId: summary.tenantId,
    tenantXId: summary.tenantXId,
    tenantName: summary.tenantName,
    tenantLogo: summary.tenantLogo,
    tenantUrl: summary.tenantUrl ?? tenantSummary.url,
    // Display priority: therapist name → center name (specific location) → tenant name
    // (organization) → raw resource name. The resource name (e.g. "Hiperbárica") is the
    // last resort because it usually reads as a service, not a place.
    name:
      summary.userName ??
      summary.centers?.[0]?.name ??
      summary.tenantName ??
      summary.resourceName,
    image: summary.userAvatarUrl ?? null,
    specialty: summary.userSpecialty ?? "",
    appointmentDate: null,
    mainAddress: summary.mainAddress ?? summary.tenantAddress ?? null,
    tenantAddress: summary.tenantAddress ?? null,
    paymentMethod: summary.paymentMethod ?? null,
    services: Array.isArray(summary.userServices) ? summary.userServices : [],
    aboutMe: Array.isArray(summary.userAbout) ? summary.userAbout : [],
    education: Array.isArray(summary.userEducation)
      ? summary.userEducation
      : [],
    reviews: [],
    consultationFee: summary.lowestPrice?.grossAmountStr ?? null,
    personalizedService: true,
    position,
    bookingOptions,
    centers: centerEntries,
    classes: [],
    totalDatesMax: 0,
    totalTimesMax: 0,
    distanceKm: summary.distanceKm ?? null,
    isOnline: summary.isOnline,
    baseDateIso,
    rawSummary: summary,
  };
}

function createTenantSummary(
  summary: AvailabilityDoctorSummary,
): AvailabilityTenantSummary {
  return {
    id: summary.tenantId,
    xId: summary.tenantXId,
    name: summary.tenantName,
    address: summary.tenantAddress,
    latitude: summary.tenantLatitude,
    longitude: summary.tenantLongitude,
    url: summary.tenantUrl ?? null,
  };
}

function resolveDoctorEntryKey(summary: AvailabilityDoctorSummary): string {
  const tenantKey = summary.tenantXId ?? summary.tenantId ?? "tenant";

  if (summary.resourceId !== undefined && summary.resourceId !== null) {
    return `${tenantKey}:${summary.resourceId}`;
  }

  if (summary.userId !== undefined && summary.userId !== null) {
    return `${tenantKey}:user-${summary.userId}`;
  }

  const fallbackName = summary.userName ?? summary.resourceName ?? "resource";
  return `${tenantKey}:${fallbackName}`;
}

function buildBookingOptions(
  summary: AvailabilityDoctorSummary,
  tenantSummary: AvailabilityTenantSummary,
  centers: AvailabilityDoctorCenter[],
): DoctorBookingOption[] {
  if (
    !Array.isArray(summary.bookingOptions) ||
    summary.bookingOptions.length === 0
  ) {
    const fallbackCenter = centers[0] ?? null;
    const fallbackKey = `${summary.resourceId}:${
      summary.bookingOptions?.[0]?.bookingTypeId ?? 0
    }`;
    return [
      createBookingOption(
        {
          key: fallbackKey,
          bookingTypeId: summary.bookingOptions?.[0]?.bookingTypeId ?? 0,
          bookingTypeName:
            summary.bookingOptions?.[0]?.bookingTypeName ??
            summary.resourceName,
          bookingTypeDescription:
            summary.bookingOptions?.[0]?.bookingTypeDescription ?? "",
          durationMinutes: summary.bookingOptions?.[0]?.durationMinutes ?? null,
          preferredDurationMinutes:
            summary.bookingOptions?.[0]?.preferredDurationMinutes ?? null,
          bookingByPlace: summary.bookingOptions?.[0]?.bookingByPlace ?? true,
          allowCreateBookByPlace:
            summary.bookingOptions?.[0]?.allowCreateBookByPlace ?? false,
          allowRemote:
            summary.bookingOptions && summary.bookingOptions[0]
              ? summary.bookingOptions[0].allowRemote === true
              : false,
          attendanceMode: toAttendanceMode(summary.bookingOptions?.[0]),
          priceDisplay:
            summary.bookingOptions?.[0]?.priceDisplay ??
            summary.lowestPrice?.grossAmountStr ??
            "",
          priceLocalized:
            summary.bookingOptions?.[0]?.priceLocalized ??
            summary.lowestPrice ??
            null,
          depositDisplay: null,
          // synthetic option built from lowestPrice, which is a minimum by construction
          priceIsFrom: true,
          tenant: tenantSummary,
          centerId: fallbackCenter?.centerId ?? null,
          centerKey:
            fallbackCenter?.centerId != null
              ? String(fallbackCenter.centerId)
              : `tenant-${summary.tenantId}`,
          locationName: fallbackCenter?.name ?? summary.tenantName ?? "",
          locationAddress:
            fallbackCenter?.address ??
            summary.tenantAddress ??
            summary.mainAddress ??
            "",
        },
        summary,
        tenantSummary,
      ),
    ];
  }

  return summary.bookingOptions.map((option) =>
    createBookingOption(option, summary, tenantSummary),
  );
}

function createBookingOption(
  option: AvailabilityBookingOptionSummary,
  summary: AvailabilityDoctorSummary,
  tenantSummary: AvailabilityTenantSummary,
): DoctorBookingOption {
  const priceSummary = option.priceLocalized ?? summary.lowestPrice ?? null;

  const priceLabel = option.priceDisplay ?? priceSummary?.grossAmountStr ?? "";

  const bookingTypeImage = option.bookingTypeImage || null;
  const bookingTypeName = option.bookingTypeName;
  const bookingTypeDescription = option.bookingTypeDescription || null;
  const rawSchedule = Array.isArray(option.schedule) ? option.schedule : [];
  const schedule = rawSchedule.filter(
    (day) => day && typeof day.date === "string",
  );
  const hours = collectScheduleHours(schedule);
  const scheduleLoaded = schedule.length > 0;

  const centerKey =
    option.centerKey ??
    (option.centerId != null
      ? String(option.centerId)
      : `tenant-${summary.tenantId}`);

  return {
    key: String(option.key ?? `${summary.resourceId}:${option.bookingTypeId}`),
    bookingTypeId: option.bookingTypeId,
    bookingTypeName,
    bookingTypeDescription,
    bookingTypeImage,
    label: bookingTypeName || summary.resourceName,
    price: priceLabel,
    priceLocalized: priceSummary,
    depositDisplay: option.depositDisplay ?? null,
    priceIsFrom: option.priceIsFrom ?? false,
    durationMinutes:
      option.preferredDurationMinutes ?? option.durationMinutes ?? null,
    bookingByPlace: option.bookingByPlace,
    allowCreateBookByPlace: option.allowCreateBookByPlace,
    allowRemote: option.allowRemote === true,
    attendanceMode: toAttendanceMode(option),
    schedule,
    hours,
    calendarWindow: null,
    tenant: option.tenant ?? tenantSummary,
    centerId: option.centerId ?? null,
    centerKey,
    location: {
      name:
        option.locationName ??
        option.tenant?.name ??
        summary.centers?.[0]?.name ??
        summary.tenantName ??
        "",
      address:
        option.locationAddress ??
        option.tenant?.address ??
        summary.centers?.[0]?.address ??
        summary.mainAddress ??
        summary.tenantAddress ??
        "",
    },
    resourceId: summary.resourceId,
    userId: summary.userId ?? null,
    scheduleLoaded,
    scheduleLoading: false,
    scheduleError: null,
  };
}

function buildCenters(
  summary: AvailabilityDoctorSummary,
  tenantSummary: AvailabilityTenantSummary,
  centers: AvailabilityDoctorCenter[],
  bookingOptions: DoctorBookingOption[],
  translate: TranslateFn,
): DoctorCenter[] {
  const variantMap = new Map<string, string[]>();
  bookingOptions.forEach((option) => {
    const key = option.centerKey;
    const existing = variantMap.get(key);
    if (existing) {
      existing.push(String(option.key));
      return;
    }
    variantMap.set(key, [String(option.key)]);
  });

  const mappedCenters = centers.map((center) => {
    const key =
      center.centerId != null
        ? String(center.centerId)
        : `tenant-${summary.tenantId}`;
    return {
      key,
      centerId: center.centerId ?? null,
      tenantId: summary.tenantId,
      tenantXId: summary.tenantXId,
      tenantName: summary.tenantName,
      name: center.name ?? summary.tenantName ?? translate("Clinic"),
      address:
        center.address ?? summary.mainAddress ?? summary.tenantAddress ?? "",
      url: summary.tenantUrl ?? tenantSummary.url ?? null,
      variantKeys: variantMap.get(key) ?? [],
      latitude: center.latitude ?? summary.tenantLatitude ?? null,
      longitude: center.longitude ?? summary.tenantLongitude ?? null,
    };
  });

  if (mappedCenters.length === 0) {
    const fallbackKey = `tenant-${summary.tenantId}`;
    mappedCenters.push({
      key: fallbackKey,
      centerId: null,
      tenantId: summary.tenantId,
      tenantXId: summary.tenantXId,
      tenantName: summary.tenantName,
      name: summary.tenantName ?? translate("Clinic"),
      address: summary.mainAddress ?? summary.tenantAddress ?? "",
      url: summary.tenantUrl ?? tenantSummary.url ?? null,
      variantKeys: Array.from(variantMap.values()).flat(),
      latitude: summary.tenantLatitude ?? null,
      longitude: summary.tenantLongitude ?? null,
    });
  }

  return mappedCenters;
}
